import { useLingui } from '@lingui/react/macro';
import classNames from 'classnames';
import { CARD_PAYMENTS_ENABLED } from 'modules/organizer/domain/constants/wallet-topup';
import React from 'react';
import { theme } from 'styles/theme/theme';
import UDFormLabel from 'ud-form/components/label';
import UDCard from 'ud-ui/components/card';
import UDText, { UDTextType } from 'ud-ui/components/text';
import {
  SUBSCRIPTION_BADGE_CLASS,
  SUBSCRIPTION_COLUMN_CLASS,
  SUBSCRIPTION_COPY,
  SUBSCRIPTION_FORM_CLASS,
  SUBSCRIPTION_HEADER_CLASS,
  SUBSCRIPTION_INFO_ROW_CLASS,
  SUBSCRIPTION_PLAN_LIST_CLASS,
  SUBSCRIPTION_PLAN_ROW_CLASS,
} from './subscription-layout';

/**
 * Сколько тарифов рисовать, пока каталог не пришёл: столько их в каталоге сейчас.
 * Ошибка в числе стоит одного сдвига карточки, а не раскладки всего экрана.
 */
export const SUBSCRIPTION_SKELETON_FALLBACK_PLAN_COUNT = 3;

export interface SubscriptionSkeletonProps {
  /**
   * Есть ли у организатора подписка, если ответ уже пришёл. От этого зависит и высота
   * второй карточки, и наличие четвёртой («Подключить тариф»). Пока ответа нет —
   * `undefined`, и рисуется вариант без подписки.
   */
  hasSubscription?: boolean;
  /** Число тарифов в каталоге, если он уже пришёл. */
  planCount?: number;
}

/**
 * Заглушка вкладки «Подписка» на время загрузки.
 *
 * Повторяет раскладку `Subscription` карточка в карточку: те же `UDCard`, те же типы
 * `UDText` и классы из `subscription-layout.ts`. Статичный текст — настоящий, данные —
 * плашки. Высота плашки текста не подбирается: плашка сама является `UDText` того же
 * типа с неразрывным пробелом внутри, поэтому её высоту даёт `line-height` этого типа.
 * Высота поля ввода и кнопки — `h-9`, 36px из `ud-form/components/input/component/styles.ts`
 * и `ud-ui/components/button/styles.ts`. Ширины плашек — среднее по реальным данным.
 */
export const SubscriptionSkeleton: React.FC<SubscriptionSkeletonProps> = ({
  hasSubscription = false,
  planCount = SUBSCRIPTION_SKELETON_FALLBACK_PLAN_COUNT,
}) => {
  const { t } = useLingui();

  return (
    <div className={SUBSCRIPTION_COLUMN_CLASS} data-testid="subscription-skeleton" aria-busy="true">
      <UDCard>
        <UDText type="headline" className="mb-2">
          {t(SUBSCRIPTION_COPY.balanceTitle)}
        </UDText>
        <SkeletonText type="title-1" className="w-44" />
      </UDCard>

      <UDCard>{hasSubscription ? <ActiveSubscriptionSkeleton /> : <InactiveSubscription />}</UDCard>

      <UDCard>
        <div className={SUBSCRIPTION_FORM_CLASS}>
          <UDText type="headline">{t(SUBSCRIPTION_COPY.topupTitle)}</UDText>
          <UDText type="body-text" color={theme.colors.Gray}>
            {t(SUBSCRIPTION_COPY.topupHint)}
          </UDText>
          <SkeletonField label={t(SUBSCRIPTION_COPY.amountLabel)} />
          <SkeletonField label={t(SUBSCRIPTION_COPY.emailLabel)} />
          <div className="flex flex-col gap-2">
            <SkeletonButton />
            {CARD_PAYMENTS_ENABLED && <SkeletonButton />}
          </div>
        </div>
      </UDCard>

      {!hasSubscription && (
        <UDCard>
          <div className={SUBSCRIPTION_FORM_CLASS}>
            <UDText type="headline">{t(SUBSCRIPTION_COPY.buyPlanTitle)}</UDText>
            <UDText type="body-text" color={theme.colors.Gray}>
              {t(SUBSCRIPTION_COPY.buyPlanHint)}
            </UDText>
            <PlanListSkeleton count={planCount} />
            <SkeletonButton />
          </div>
        </UDCard>
      )}
    </div>
  );
};

const ActiveSubscriptionSkeleton = () => {
  const { t } = useLingui();

  return (
    <>
      <div className={SUBSCRIPTION_HEADER_CLASS}>
        {/* `h-6` — как у заголовка экрана: бейдж на 24px выше строки заголовка в 22px. */}
        <UDText className="h-6" type="headline">
          {t(SUBSCRIPTION_COPY.activeTitle)}
        </UDText>
        {/* Ширину бейджа задаёт его подпись под теми же отступами. */}
        <span className={classNames(SUBSCRIPTION_BADGE_CLASS, 'skeleton-block text-transparent')} aria-hidden>
          {t(SUBSCRIPTION_COPY.activeBadge)}
        </span>
      </div>
      <InfoRowSkeleton label={t(SUBSCRIPTION_COPY.activeFrom)} valueClassName="w-20" />
      <InfoRowSkeleton label={t(SUBSCRIPTION_COPY.activeTo)} valueClassName="w-20" />
      <InfoRowSkeleton label={t(SUBSCRIPTION_COPY.plan)} valueClassName="w-16" />
    </>
  );
};

/** Карточка без подписки целиком статична — рисуется как есть. */
const InactiveSubscription = () => {
  const { t } = useLingui();

  return (
    <>
      <UDText type="headline" className="mb-2">
        {t(SUBSCRIPTION_COPY.inactiveTitle)}
      </UDText>
      <UDText type="body-text" color={theme.colors.Gray}>
        {t(SUBSCRIPTION_COPY.inactiveHint)}
      </UDText>
    </>
  );
};

const InfoRowSkeleton = ({ label, valueClassName }: { label: string; valueClassName: string }) => (
  <div className={SUBSCRIPTION_INFO_ROW_CLASS}>
    <UDText type="footnote" color={theme.colors.Gray}>
      {label}
    </UDText>
    <SkeletonText type="subhead" className={valueClassName} />
  </div>
);

const PlanListSkeleton = ({ count }: { count: number }) => {
  const { t } = useLingui();

  if (count === 0) {
    return (
      <UDText type="body-text" color={theme.colors.Gray}>
        {t(SUBSCRIPTION_COPY.noPlans)}
      </UDText>
    );
  }

  return (
    <div className={SUBSCRIPTION_PLAN_LIST_CLASS}>
      {Array.from({ length: count }, (_, index) => (
        <UDCard key={index}>
          <div className={SUBSCRIPTION_PLAN_ROW_CLASS} data-testid="subscription-skeleton-plan">
            <div>
              <SkeletonText type="subhead" className="w-36" />
              <SkeletonText type="footnote" className="w-28" />
            </div>
            {/* Первый тариф выбран по умолчанию (`pickDefaultPlan`), пока подписки нет. */}
            {index === 0 && (
              <UDText type="footnote" color={theme.colors.Gray}>
                {t(SUBSCRIPTION_COPY.selectedPlan)}
              </UDText>
            )}
          </div>
        </UDCard>
      ))}
    </div>
  );
};

/** Поле `ud-form`: та же обёртка, что у `UDFormFieldWrapper`, контрол — плашка в 36px. */
const SkeletonField = ({ label }: { label: string }) => (
  <div className="flex w-full flex-col">
    <div className="flex items-center">
      <UDFormLabel className="mb-2">{label}</UDFormLabel>
    </div>
    <div className="skeleton-block h-9 w-full" />
  </div>
);

const SkeletonButton = () => <div className="skeleton-block h-9 w-full" />;

/** Плашка на месте текста: `UDText` того же типа, высоту даёт его `line-height`. */
const SkeletonText = ({ type, className }: { type: UDTextType; className: string }) => (
  <UDText type={type} className={classNames('skeleton-block', className)} aria-hidden>
    {' '}
  </UDText>
);

export default SubscriptionSkeleton;
